import React from "react";
import { View, Text, StyleSheet } from "@react-pdf/renderer";
import moment from "moment";

// Excel-style Monochrome / Grayscale Palette
const C = {
  textMain: "#0f172a",      // Deep slate / near black
  textMuted: "#475569",     // Neutral slate for secondary labels
  textSub: "#64748b",       // Muted gray for timestamps
  border: "#cbd5e1",        // Clean Excel-like cell border
  borderLight: "#e2e8f0",   // Lighter row separator
  headerBg: "#e2e8f0",      // Classic Excel column header gray
  headerBgLight: "#f1f5f9", // Secondary card header gray
  white: "#ffffff",
  rowAlt: "#f8fafc",        // Subtle alternating row
  danger: "#b91c1c",        // Dark red for negative deduction values
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 18,
    paddingTop: 6,
    paddingBottom: 12,
    backgroundColor: C.white,
    fontFamily: "Helvetica",
  },

  // ─── Header: Title & Meta ─────────────────────────────────
  titleContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 5,
  },
  titleLeft: {
    flexDirection: "column",
  },
  invoiceTitle: {
    fontSize: 14,
    fontWeight: "normal",
    color: C.textMain,
    letterSpacing: 0.3,
  },
  invoiceSubtitle: {
    fontSize: 8,
    fontWeight: "normal",
    color: C.textMuted,
    marginTop: 2,
  },
  titleRightBox: {
    backgroundColor: C.rowAlt,
    borderWidth: 0.75,
    borderColor: C.border,
    borderRadius: 1,
    paddingVertical: 3.5,
    paddingHorizontal: 8,
    minWidth: 140,
  },
  metaRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginVertical: 1,
  },
  metaLabel: {
    fontSize: 7,
    fontWeight: "normal",
    color: C.textMuted,
  },
  metaValue: {
    fontSize: 7,
    fontWeight: "normal",
    color: C.textMain,
  },
  titleDivider: {
    height: 1,
    backgroundColor: C.border,
    marginBottom: 7,
  },

  // ─── Billing Section (Bill From & Bill To) ─────────────────
  billingContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 7,
  },
  billingCard: {
    width: "48.5%",
    borderWidth: 0.75,
    borderColor: C.border,
    borderRadius: 1,
    overflow: "hidden",
  },
  cardHeader: {
    backgroundColor: C.headerBg,
    borderBottomWidth: 0.75,
    borderBottomColor: C.border,
    paddingVertical: 3,
    paddingHorizontal: 6,
  },
  cardHeaderText: {
    fontSize: 8,
    fontWeight: "normal",
    color: C.textMain,
    letterSpacing: 0.2,
  },
  cardBody: {
    paddingVertical: 4,
    paddingHorizontal: 6,
  },
  partyName: {
    fontSize: 7.5,
    fontWeight: "normal",
    color: C.textMain,
    marginBottom: 1.5,
  },
  partyDetail: {
    fontSize: 7,
    fontWeight: "normal",
    color: C.textMuted,
    lineHeight: 1.3,
  },

  // ─── Items Table (Excel Sheet Grid Layout) ─────────────────
  sectionTitle: {
    fontSize: 8,
    fontWeight: "normal",
    color: C.textMain,
    marginBottom: 3,
    letterSpacing: 0.2,
  },
  table: {
    borderWidth: 0.75,
    borderColor: C.border,
    borderRadius: 1,
    overflow: "hidden",
    marginBottom: 7,
  },
  tableHeader: {
    flexDirection: "row",
    alignItems: "stretch",
    backgroundColor: C.headerBg,
    borderBottomWidth: 0.75,
    borderBottomColor: C.border,
    minHeight: 15,
  },
  thCell: {
    fontSize: 7,
    fontWeight: "normal",
    color: C.textMain,
    paddingVertical: 3,
    paddingHorizontal: 3,
    borderRightWidth: 0.5,
    borderRightColor: C.border,
    justifyContent: "center",
  },
  tableRow: {
    flexDirection: "row",
    alignItems: "stretch",
    borderBottomWidth: 0.5,
    borderBottomColor: C.border,
    minHeight: 14,
  },
  tdCell: {
    fontSize: 7,
    fontWeight: "normal",
    color: C.textMain,
    paddingVertical: 2.5,
    paddingHorizontal: 3,
    borderRightWidth: 0.5,
    borderRightColor: C.border,
    justifyContent: "center",
  },

  // ─── Bottom Section (Bank Details & Summary) ───────────────
  bottomContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 7,
  },
  paymentCard: {
    width: "48.5%",
    borderWidth: 0.75,
    borderColor: C.border,
    borderRadius: 1,
    overflow: "hidden",
  },
  bankField: {
    flexDirection: "row",
    alignItems: "center",
    marginVertical: 1,
  },
  bankLabel: {
    fontSize: 7,
    fontWeight: "normal",
    color: C.textMuted,
    width: "40%",
  },
  bankValue: {
    fontSize: 7,
    fontWeight: "normal",
    color: C.textMain,
    width: "60%",
  },

  summaryCard: {
    width: "48.5%",
    borderWidth: 0.75,
    borderColor: C.border,
    borderRadius: 1,
    overflow: "hidden",
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 3,
    paddingHorizontal: 6,
    borderBottomWidth: 0.5,
    borderBottomColor: C.borderLight,
  },
  summaryLabel: {
    fontSize: 7,
    fontWeight: "normal",
    color: C.textMuted,
  },
  summaryValue: {
    fontSize: 7,
    fontWeight: "normal",
    color: C.textMain,
  },
  deductionLabel: {
    fontSize: 7,
    fontWeight: "normal",
    color: C.danger,
  },
  deductionValue: {
    fontSize: 7,
    fontWeight: "normal",
    color: C.danger,
  },
  summaryLabelBold: {
    fontSize: 7.5,
    fontWeight: "normal",
    color: C.textMain,
  },
  summaryValueBold: {
    fontSize: 7.5,
    fontWeight: "normal",
    color: C.textMain,
  },
  balanceDueRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: C.headerBg,
    paddingVertical: 4,
    paddingHorizontal: 6,
    borderTopWidth: 0.75,
    borderTopColor: C.border,
  },
  balanceDueLabel: {
    fontSize: 8,
    fontWeight: "normal",
    color: C.textMain,
    letterSpacing: 0.2,
  },
  balanceDueValue: {
    fontSize: 8,
    fontWeight: "normal",
    color: C.textMain,
  },

  // ─── Footer ────────────────────────────────────────────────
  footerContainer: {
    marginTop: 6,
    paddingTop: 5,
    borderTopWidth: 0.5,
    borderTopColor: C.borderLight,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  footerNote: {
    fontSize: 6,
    fontWeight: "normal",
    color: C.textSub,
    fontStyle: "italic",
  },
  footerPageInfo: {
    fontSize: 6,
    fontWeight: "normal",
    color: C.textSub,
  },
});

const InvoicePdf = ({ invoiceDetails = {} }) => {
  const {
    userDetails = {},
    counterParty = {},
    deductions = [],
    bankDetails = {},
    totals = {},
    invoiceDetail = [],
    invoiceNo = "",
  } = invoiceDetails;

  const formatCurrency = (amt) =>
    parseFloat(amt || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const displayInvoiceNo = invoiceNo || `INV-${moment().format("YYYYMMDD")}`;
  const displayDate = moment().format("DD/MM/YYYY");

  const billFromName =
    userDetails?.personal?.firstName
      ? `${userDetails.personal.firstName} ${userDetails?.personal?.lastName || ""}`.trim()
      : (userDetails?.organization?.name || "Billing Party");

  const billToName = counterParty?.details?.name || "Unknown Counterparty";

  // Build clean bank fields list, completely filtering out any empty labels/values
  const bankFields = [
    { label: "Currency", value: bankDetails?.currency || "USD" },
    bankDetails?.bankName ? { label: "Bank Name", value: bankDetails.bankName } : null,
    bankDetails?.accountName ? { label: "Account Name", value: bankDetails.accountName } : null,
    bankDetails?.accountNumber ? { label: "Account No", value: bankDetails.accountNumber } : null,
    bankDetails?.iban ? { label: "IBAN", value: bankDetails.iban } : null,
    bankDetails?.ifscCode ? { label: "IFSC Code", value: bankDetails.ifscCode } : null,
    bankDetails?.branch ? { label: "Branch", value: bankDetails.branch } : null,
    ...(bankDetails?.extra
      ?.filter((item) => item && (item.label || item.value))
      ?.map((item) => ({
        label: item.label || "Other",
        value: item.value || "-",
      })) || []),
  ].filter(Boolean);

  return (
    <View style={styles.container}>
      {/* ── 1. Title & Meta Box ── */}
      <View style={styles.titleContainer}>
        <View style={styles.titleLeft}>
          <Text style={styles.invoiceTitle}>Invoice</Text>
          <Text style={styles.invoiceSubtitle}>
            {userDetails?.organization?.name || "Maritime Freight Services"}
          </Text>
        </View>
        <View style={styles.titleRightBox}>
          <View style={styles.metaRow}>
            <Text style={styles.metaLabel}>Invoice No:</Text>
            <Text style={styles.metaValue}>{displayInvoiceNo}</Text>
          </View>
          <View style={styles.metaRow}>
            <Text style={styles.metaLabel}>Date:</Text>
            <Text style={styles.metaValue}>{displayDate}</Text>
          </View>
        </View>
      </View>

      {/* Thin Separator Line */}
      <View style={styles.titleDivider} />

      {/* ── 2. Bill From & Bill To ── */}
      <View style={styles.billingContainer}>
        {/* Bill From */}
        <View style={styles.billingCard}>
          <View style={styles.cardHeader}>
            <Text style={styles.cardHeaderText}>Bill From</Text>
          </View>
          <View style={styles.cardBody}>
            <Text style={styles.partyName}>{billFromName}</Text>
            {userDetails?.organization?.name && billFromName !== userDetails.organization.name && (
              <Text style={styles.partyDetail}>{userDetails.organization.name}</Text>
            )}
            {userDetails?.personal?.email && (
              <Text style={styles.partyDetail}>{userDetails.personal.email}</Text>
            )}
            {userDetails?.personal?.phone && (
              <Text style={styles.partyDetail}>{userDetails.personal.phone}</Text>
            )}
            {!userDetails?.organization?.name && !userDetails?.personal?.email && (
              <Text style={styles.partyDetail}>-</Text>
            )}
          </View>
        </View>

        {/* Bill To */}
        <View style={styles.billingCard}>
          <View style={styles.cardHeader}>
            <Text style={styles.cardHeaderText}>Bill To</Text>
          </View>
          <View style={styles.cardBody}>
            <Text style={styles.partyName}>{billToName}</Text>
            {counterParty?.details?.contactPerson && (
              <Text style={styles.partyDetail}>Contact: {counterParty.details.contactPerson}</Text>
            )}
            {counterParty?.details?.email && (
              <Text style={styles.partyDetail}>{counterParty.details.email}</Text>
            )}
            {counterParty?.details?.phone && (
              <Text style={styles.partyDetail}>{counterParty.details.phone}</Text>
            )}
            {!counterParty?.details?.contactPerson && !counterParty?.details?.email && (
              <Text style={styles.partyDetail}>-</Text>
            )}
          </View>
        </View>
      </View>

      {/* ── 3. Invoice Line Items (Excel-like Gray Header Grid) ── */}
      <Text style={styles.sectionTitle}>Invoice Line Items</Text>
      <View style={styles.table}>
        {/* Table Header */}
        <View style={styles.tableHeader}>
          <Text style={[styles.thCell, { width: "4%", textAlign: "center" }]}>#</Text>
          <Text style={[styles.thCell, { width: "18%", textAlign: "left" }]}>Item</Text>
          <Text style={[styles.thCell, { width: "23%", textAlign: "left" }]}>Description</Text>
          <Text style={[styles.thCell, { width: "9%", textAlign: "center" }]}>B/L Date</Text>
          <Text style={[styles.thCell, { width: "8%", textAlign: "center" }]}>B/L Qty</Text>
          <Text style={[styles.thCell, { width: "10%", textAlign: "center" }]}>Port</Text>
          <Text style={[styles.thCell, { width: "9%", textAlign: "center" }]}>Operation</Text>
          <Text style={[styles.thCell, { width: "9%", textAlign: "center" }]}>Cargo/Vessel</Text>
          <Text style={[styles.thCell, { width: "10%", textAlign: "right", borderRightWidth: 0 }]}>
            Amount
          </Text>
        </View>

        {/* Table Rows */}
        {invoiceDetail?.map((item, idx) => (
          <View
            key={idx}
            style={[styles.tableRow, idx % 2 !== 0 ? { backgroundColor: C.rowAlt } : {}]}
          >
            <Text style={[styles.tdCell, { width: "4%", textAlign: "center" }]}>{idx + 1}</Text>
            <Text
              style={[
                styles.tdCell,
                { width: "18%", textAlign: "left" },
              ]}
            >
              {item?.invoiceDetail?.label || item?.cargoName || "Service"}
            </Text>
            <Text style={[styles.tdCell, { width: "23%", textAlign: "left" }]}>
              {item?.invoiceDetail?.description || "-"}
            </Text>
            <Text style={[styles.tdCell, { width: "9%", textAlign: "center" }]}>
              {item?.invoiceDetail?.blDate
                ? moment(item.invoiceDetail.blDate).format("DD/MM/YYYY")
                : "-"}
            </Text>
            <Text style={[styles.tdCell, { width: "8%", textAlign: "center" }]}>
              {item?.invoiceDetail?.blQty || "-"}
            </Text>
            <Text style={[styles.tdCell, { width: "10%", textAlign: "center" }]}>
              {item?.port || "-"}
            </Text>
            <Text style={[styles.tdCell, { width: "9%", textAlign: "center" }]}>
              {item?.operation || "-"}
            </Text>
            <Text style={[styles.tdCell, { width: "9%", textAlign: "center" }]}>
              {item?.cargoName || (item?.vesselId ? "Vessel" : "-")}
            </Text>
            <Text
              style={[
                styles.tdCell,
                { width: "10%", textAlign: "right", borderRightWidth: 0 },
              ]}
            >
              {formatCurrency(item?.invoiceDetail?.amt)}
            </Text>
          </View>
        ))}
      </View>

      {/* ── 4. Bottom Section: Bank Details & Totals ── */}
      <View style={styles.bottomContainer}>
        {/* Left: Payment & Bank Details */}
        <View style={styles.paymentCard}>
          <View style={styles.cardHeader}>
            <Text style={styles.cardHeaderText}>Payment & Bank Details</Text>
          </View>
          <View style={styles.cardBody}>
            {bankFields.map((field, i) => (
              <View key={i} style={styles.bankField}>
                <Text style={styles.bankLabel}>{field.label}:</Text>
                <Text style={styles.bankValue}>{field.value}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Right: Summary / Totals */}
        <View style={styles.summaryCard}>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Subtotal</Text>
            <Text style={styles.summaryValue}>{formatCurrency(totals?.subtotal)}</Text>
          </View>
          {deductions?.map((d, i) => (
            <View key={i} style={styles.summaryRow}>
              <Text style={styles.deductionLabel}>{d?.label || "Deduction"}</Text>
              <Text style={styles.deductionValue}>-{formatCurrency(d?.value)}</Text>
            </View>
          ))}
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabelBold}>Invoice Total</Text>
            <Text style={styles.summaryValueBold}>{formatCurrency(totals?.finalTotal)}</Text>
          </View>
          <View style={styles.balanceDueRow}>
            <Text style={styles.balanceDueLabel}>Balance Due</Text>
            <Text style={styles.balanceDueValue}>{formatCurrency(totals?.finalTotal)}</Text>
          </View>
        </View>
      </View>

      {/* ── 5. Footer ── */}
      <View style={styles.footerContainer}>
        <Text style={styles.footerNote}>
          Thank you for your business. Please remit payment as per the bank details above.
        </Text>
        <Text style={styles.footerPageInfo}>
          Page 1 of 1 • Generated on {moment().format("DD MMM YYYY, HH:mm")}
        </Text>
      </View>
    </View>
  );
};

export default InvoicePdf;